import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, Checkbox } from '../../../../../components';
import { Button, Container, Dialog, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { request, toast } from '../../../../../utils';
import { CustomButtonn } from '../../../../../components';
import { useLocation } from 'react-router-dom';

const initialTaxCategoryDataObj = {
  data: [],
  loading: false
};

const TABLE_COLUMNS = [
  {
    header: 'Tax Category Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.tax_category_name}`}
        </span>
      );
    }
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

export const StoreTaxCategory = ({ getTaxCategoryDropdown, setTaxCategoryDialogue }) => {
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [taxCategoryDataObj, setTaxCategoryDataObj] = useState(initialTaxCategoryDataObj);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({ tax_category_name: Yup.string().required('Tax Category Name is required') })
    ),
    mode: 'all'
  });
  const { handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    if (selectedRecord) {
      setValue('tax_category_name', selectedRecord.tax_category_name);
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/store-tax-category-master-update' : '/store-tax-category-master-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        tax_category_name: formValues.tax_category_name
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Tax Category Details`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
      handleFetchList();
      getTaxCategoryDropdown();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Tax Category Details`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/store-tax-category-master-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancelled Tax Category master', { variant: 'success' });
      setSelectedRecord();
      handleFetchList();
    } else {
      toast('Failed to cancel Tax Category master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/store-tax-category-master-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Tax Category master', { variant: 'success' });
      setSelectedRecord();
      handleFetchList();
    } else {
      toast('Failed to delete Tax Category master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleFetchList = async () => {
    setOpenQueryDialog(true);
    setTaxCategoryDataObj({ loading: true });
    const response = await request('/store-tax-category-master-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setTaxCategoryDataObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setTaxCategoryDataObj({ loading: false });
    }
  };
  useEffect(() => {
    handleFetchList();
  }, []);
  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto',
          maxWidth: '100vw',
          minHeight: '100vh'
        }}
      >
        <Container
          sx={{
            mt: 2,
            p: 0,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pb: 0.5,
                mb: 1,
                borderBottom: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Tax Category Details
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }} spacing={1}>
                <CustomButtonn
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    reset();
                  }}
                  id={`${lastPathSegment}Write`}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                    setTaxCategoryDialogue(false);
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Stack spacing={2} sx={{ marginBottom: '1%' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography sx={{ flex: 0.4, fontSize: '1.6rem', fontWeight: 700 }}>Tax Category</Typography>
                <TextField
                  name="tax_category_name"
                  sx={{
                    flex: 0.8,
                    border: currentAction ? '0.5px solid red' : '',
                    backgroundColor: currentAction ? '#B3E5FC' : ''
                  }}
                  inputProps={{ sx: { fontSize: '1.4rem' } }}
                  placeholder="Tax Category Name"
                  fullWidth
                  disabled={!['new', 'edit'].includes(currentAction)}
                />
              </Stack>
            </Stack>
            <Stack
              padding={0}
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              borderBottom="1px solid"
              borderColor="grey.300"
            ></Stack>
            <Table
              sx={{ mt: 0.5 }}
              columns={TABLE_COLUMNS}
              data={taxCategoryDataObj.data || []}
              loading={taxCategoryDataObj.loading}
              onRowClick={(row) => {
                setSelectedRecord(row);
                setOpenQueryDialog(false);
              }}
            />
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
